import type { PolicyProperty } from '@policyvision/contracts';
import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ValidableSection } from '@/policy/component/Validation/ValidableSection';
import { useScroll } from '@/policy/hooks/useScroll';
import { Policy } from '@/policy/types/PolicyCoverage';
import { ValidationResult } from '@/policy/types/ValidationResult';

import { ScrollShadows } from '../ScrollShadows/ScrollShadows';
interface PolicyInformationSectionProps {
  section: Policy;
  validation: ValidationResult;
  onValidationChange: (validation: ValidationResult) => void;
  isSelected: boolean;
  onTableClick: (sectionUuid: string) => void;
}

export const PolicyInformationSection = forwardRef<
  HTMLDivElement,
  PolicyInformationSectionProps
>(
  (
    { section, validation, onValidationChange, isSelected, onTableClick },
    ref,
  ) => {
    const { t } = useTranslation();
    const { scrollContainerRef, dragProps, canScrollLeft, canScrollRight } =
      useScroll();
    return (
      <ValidableSection
        ref={ref}
        title={t('policy.policyDetailsTitle')}
        sectionIdentifier={section.identifier}
        validation={validation}
        onValidationChange={onValidationChange}
        isSelected={isSelected}
        onTableClick={onTableClick}
      >
        <ScrollShadows
          canScrollLeft={canScrollLeft}
          canScrollRight={canScrollRight}
        >
          <div
            ref={scrollContainerRef}
            {...dragProps}
            className={`w-full overflow-x-auto scrollbar-visible px-[32px] py-[8px] select-none ${dragProps.className}`}
          >
            <table className="text-xxs">
              <tbody>
                {section.properties.map((property: PolicyProperty) => {
                  const key = `policy.policyDetails.${property.key}`;
                  return (
                    <React.Fragment key={property.key}>
                      <tr className="h-2" />
                      <tr>
                        <td className="w-[200px] pr-2 font-semibold align-top">
                          {t(key, { defaultValue: property.key })}
                        </td>
                        <td className="pl-2 align-top">{property.value}</td>
                      </tr>
                    </React.Fragment>
                  );
                })}
                <tr className="h-2" />
              </tbody>
            </table>
          </div>
        </ScrollShadows>
      </ValidableSection>
    );
  },
);

PolicyInformationSection.displayName = 'PolicyInformationSection';
